// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * FuelContext - Centralized Fuel State Management
 * Replaces global FuelManager singleton with React Context
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import GameConfig from '../config/GameConfig';
import FuelMonitor from '../utils/game/FuelMonitor';
import { useDebouncedStorage } from '../hooks/useDebouncedStorage';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

const FuelContext = createContext(null);

export function FuelProvider({ children }) {
  // Initialize from localStorage or defaults
  const [fuel, setFuel] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.FUEL);
    return saved !== null ? parseFloat(saved) : GameConfig.fuel.maxFuelCapacity;
  });

  const [maxFuel, setMaxFuel] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.MAX_FUEL_CAPACITY);
    return saved !== null ? parseFloat(saved) : GameConfig.fuel.maxFuelCapacity;
  });

  const [tankUpgrades, setTankUpgrades] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.TANK_UPGRADES);
    return saved !== null ? parseInt(saved, 10) : 0;
  });

  // Debounced localStorage persistence
  useDebouncedStorage(STORAGE_KEYS.FUEL, fuel, 2000);
  useDebouncedStorage(STORAGE_KEYS.MAX_FUEL_CAPACITY, maxFuel, 5000);
  useDebouncedStorage(STORAGE_KEYS.TANK_UPGRADES, tankUpgrades, 5000);

  // Monitor fuel levels for voice announcements
  useEffect(() => {
    const fuelPercent = (fuel / maxFuel) * 100;
    FuelMonitor.update(fuelPercent);
  }, [fuel, maxFuel]);

  // Add fuel (clamped to max)
  const addFuel = useCallback((amount) => {
    setFuel(prev => {
      const newFuel = Math.min(prev + amount, maxFuel);
      return Math.max(0, newFuel);
    });
  }, [maxFuel]);

  // Remove fuel (clamped to 0)
  const removeFuel = useCallback((amount) => {
    setFuel(prev => {
      const newFuel = prev - amount;
return Math.max(0, newFuel);
    });
  }, []);

  // Set fuel directly (for refueling, etc.)
  const setFuelDirect = useCallback((amount) => {
    setFuel(Math.max(0, Math.min(amount, maxFuel)));
  }, [maxFuel]);

  // Refuel to full
  const refuelToFull = useCallback(() => {
    setFuel(maxFuel);
    // Trigger refueling complete voice after a short delay
    setTimeout(() => {
      FuelMonitor.triggerRefuelingComplete();
    }, 500);
  }, [maxFuel]);

  // Upgrade max fuel capacity
  const upgradeMaxFuel = useCallback((increasePercent) => {
    setMaxFuel(prev => {
      const newMax = prev * (1 + increasePercent / 100);
      return newMax;
    });
  }, []);

  // Check if we have enough fuel
  const hasFuel = useCallback((amount) => {
    return fuel >= amount;
  }, [fuel]);

  // Get fuel percentage (0-100)
  const getFuelPercent = useCallback(() => {
    return (fuel / maxFuel) * 100;
  }, [fuel, maxFuel]);

  // Check if fuel is low
  const isLowFuel = useCallback(() => {
    return fuel <= GameConfig.fuel.lowFuelThreshold;
  }, [fuel]);

  // Check if fuel is critical
  const isCriticalFuel = useCallback(() => {
    return fuel <= GameConfig.fuel.criticalFuelThreshold;
  }, [fuel]);

  // Reset to defaults (for game reset)
  const resetFuel = useCallback(() => {
    setFuel(GameConfig.fuel.maxFuelCapacity);
    setMaxFuel(GameConfig.fuel.maxFuelCapacity);
    setTankUpgrades(0);
    localStorage.removeItem(STORAGE_KEYS.FUEL);
    localStorage.removeItem(STORAGE_KEYS.MAX_FUEL_CAPACITY);
    localStorage.removeItem(STORAGE_KEYS.TANK_UPGRADES);
  }, []);

  // Tank upgrade methods
  const getTankUpgrades = useCallback(() => {
    return tankUpgrades;
  }, [tankUpgrades]);

  const getTankUpgradeInfo = useCallback(() => {
    const isMaxed = tankUpgrades >= GameConfig.fuel.maxTankUpgrades;
    
    // Calculate cost and increase based on current upgrade level
    let cost, increase, nextMax;
    if (tankUpgrades === 0) {
      // First upgrade (fuel_tank_1): +25% capacity
      cost = 800; // From UpgradesConfig
      increase = 25;
      nextMax = GameConfig.fuel.maxFuelCapacity * 1.25; // 125%
    } else if (tankUpgrades === 1) {
      // Second upgrade (fuel_tank_2): +50% additional capacity  
      cost = 1600; // From UpgradesConfig
      increase = 50;
      nextMax = GameConfig.fuel.maxFuelCapacity * 1.75; // 175% (25% + 50% bonus)
    } else {
      // No more upgrades
      cost = 0;
      increase = 0;
      nextMax = maxFuel;
    }
    
    const currentMax = maxFuel;
    
    return { 
      canUpgrade: !isMaxed, 
      isMaxed,
      reason: isMaxed ? 'Maximum upgrades reached' : null,
      cost, 
      increase,
      currentMax,
      nextMax,
      upgradesRemaining: GameConfig.fuel.maxTankUpgrades - tankUpgrades
    };
  }, [tankUpgrades, maxFuel]);

  const canPurchaseTankUpgrade = useCallback(() => {
    return tankUpgrades < GameConfig.fuel.maxTankUpgrades;
  }, [tankUpgrades]);

  const purchaseTankUpgrade = useCallback(() => {
    if (tankUpgrades >= GameConfig.fuel.maxTankUpgrades) {
      return { success: false, error: 'Maximum upgrades reached' };
    }
    
    // Apply the correct capacity increase based on upgrade level
    if (tankUpgrades === 0) {
      // First upgrade: 100% -> 125% (25% increase)
      setMaxFuel(GameConfig.fuel.maxFuelCapacity * 1.25);
    } else if (tankUpgrades === 1) {
      // Second upgrade: 125% -> 175% (additional 50% of base capacity)
      setMaxFuel(GameConfig.fuel.maxFuelCapacity * 1.75);
    }
    
    setTankUpgrades(prev => prev + 1);
    
    return { success: true, newLevel: tankUpgrades + 1 };
  }, [tankUpgrades]);

  const value = {
    fuel,
    maxFuel,
    tankUpgrades,
    addFuel,
    removeFuel,
    setFuel: setFuelDirect,
    refuelToFull,
    upgradeMaxFuel,
    hasFuel,
    getFuelPercent,
    isLowFuel,
    isCriticalFuel,
    resetFuel,
    getTankUpgrades,
    getTankUpgradeInfo,
    canPurchaseTankUpgrade,
    purchaseTankUpgrade,
  };

  return (
    <FuelContext.Provider value={value}>
      {children}
    </FuelContext.Provider>
  );
}

// Custom hook to use fuel context
export function useFuel() {
  const context = useContext(FuelContext);
  if (!context) {
    throw new Error('useFuel must be used within a FuelProvider');
  }
  return context;
}

export default FuelContext;
